import React, { useEffect, useState, useMemo } from 'react';
import '../styles.css';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getMetadataBundleForStaticPage } from '../metadata/SEOmetadata.js';
import { t } from '../i18n/translationStrings.js';

console.log("📦 HomePage component loaded");

if (process.env.NODE_ENV !== 'production') {
    console.log("✅ ENV S3_BUCKET_URL:", process.env.REACT_APP_S3_BUCKET_URL);
}

const HomePage = () => {

	if (process.env.NODE_ENV === 'development') {
	console.log("🚀 HomePage rendered");
	}
    const [books, setBooks] = useState([]);
    
    useEffect(() => {
        const fetchBooks = async () => {
            try {
                const s3Url = process.env.REACT_APP_S3_BUCKET_URL;
                if (!s3Url) {
                    console.error("❌ ERROR: REACT_APP_S3_BUCKET_URL is not set. Check your .env file.");
                    return;
                }

                const formattedUrl = s3Url.endsWith("/") 
                    ? `${s3Url}HermesTest/metadata.json`
                    : `${s3Url}/HermesTest/metadata.json`;

                if (process.env.NODE_ENV !== 'production') {
                    console.log("🔗 Final S3 Fetch URL:", formattedUrl);
                }

                // ✅ Fetch in parallel
                const [localResponse, s3Response] = await Promise.all([
                    fetch('/ProductMetaData.json'),
                    fetch(formattedUrl),
                ]);

                let rawLocalBooks = [];
                if (localResponse.ok) {
                    rawLocalBooks = await localResponse.json();
                } else {
                    console.error(`Local fetch failed with status ${localResponse.status}`);
                }

                let rawS3Books;
                if (s3Response.ok) {
                    try {
                        rawS3Books = await s3Response.json();
                        if (process.env.NODE_ENV !== 'production') {
                            console.log("✅ Parsed S3 Books:", rawS3Books);
                        }
                    } catch (error) {
                        console.error("❌ JSON parse error for S3 response:", error);
                        return;
                    }
                } else {
                    throw new Error(`Failed to fetch metadata.json: ${s3Response.statusText}`);
                }

                const formattedLocalBooks = rawLocalBooks.map(book => ({ ...book, source: 'local' }));
                const formattedS3Books = rawS3Books.map(book => ({ ...book, source: 's3' }));

                formattedS3Books.forEach(book => {
                    if (!book.titleSlug) {
                        book.titleSlug = book.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                    }
                });
                formattedLocalBooks.forEach(book => {
                    if (!book.titleSlug) {
                        book.titleSlug = book.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                    }
                });

                const englishBooks = [...formattedS3Books, ...formattedLocalBooks].filter(book => book.language === "en");

                const filteredS3Books = englishBooks.filter(book => book.source === 's3');
                const filteredLocalBooks = englishBooks.filter(book => book.source !== 's3');

                const shuffledS3 = filteredS3Books.sort(() => 0.5 - Math.random());
                const shuffledLocal = filteredLocalBooks.sort(() => 0.5 - Math.random());

                // ✅ Temporary override to use 12 S3 books only
                const selectedBooks = shuffledS3.slice(0, 12);

                /* 🧩 Restore later if you want to reintroduce local books:
                const topS3 = shuffledS3.slice(0, 4);
                const moreS3 = shuffledS3.slice(4, 8);
                const localPick = shuffledLocal.slice(0, 4);

                const selectedBooks = [
                    ...topS3,
                    ...[...moreS3, ...localPick].sort(() => 0.5 - Math.random())
                ];
                */

                setBooks(selectedBooks);
            } catch (error) {
                console.error('Error fetching books:', error);
            }
        };

        fetchBooks();
    }, []);

    const uniqueCategories = useMemo(() => {
        return [...new Set([...books].flatMap(b => b.categories || []))].slice(0, 48);
    }, [books]);

    const truncateText = (text, maxLength) => {
        if (!text || text.length <= maxLength) return text;
        const truncated = text.slice(0, maxLength);
        return truncated.slice(0, truncated.lastIndexOf(' ')) + '...';
    };
	
	const pageMetadata = getMetadataBundleForStaticPage('home');

    return (
        <main className="homepage" style={{ display: 'flex', flexWrap: 'wrap', paddingTop: '20px' }}>
            <Helmet>
                <title>{pageMetadata.title}</title>
                <meta name="robots" content="index, follow" />
                <meta name="description" content={pageMetadata.description} />
				<meta name="keywords" content={Array.isArray(pageMetadata.keywords) ? pageMetadata.keywords.join(', ') : pageMetadata.keywords} />
				<meta property="og:image" content={pageMetadata.ogImage} />
                <link rel="icon" href="/favicon.ico" />
                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                <link rel="manifest" href="/site.webmanifest" />
				<link rel="canonical" href="https://www.hermesreads.com/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "HermesReads",
                        "url": "https://hermesreads.com",
                        "logo": "https://hermesreads.com/android-chrome-512x512.png"
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "name": "HermesReads",
                        "url": "https://hermesreads.com/",
                        "description": "HermesReads offers over 10,000 public domain books, most freely hosted or linked directly. A handful are Hermes editions on Amazon.",
                        "potentialAction": {
                            "@type": "SearchAction",
                            "target": "https://hermesreads.com/search?query={search_term_string}",
                            "query-input": "required name=search_term_string"
                        }
                    })}
                </script>
                {/*
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "HermesReads Printed and Kindle Editions",
                        "image": "https://hermesreads.com/images/Hermes44.webp",
                        "description": "HermesReads offers select printed and Kindle editions on Amazon to support our work."
                        "sku": "hermes-amazon-editions",
                        "url": "https://hermesreads.com/shop",
                        "offers": {
                            "@type": "Offer",
                            "price": "9.99",
                            "priceCurrency": "USD",
                            "availability": "https://schema.org/InStock",
                            "url": "https://hermesreads.com/shop"
                        }
                    })}
                </script>
                */}
            </Helmet>

            {/* Side Menu */}
            <aside
                className="sidebar"
                style={{
                    width: '230px',
                    padding: '1rem',
                    backgroundColor: 'var(--dark-gray)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    marginRight: '1rem',
                }}
            >
                <h3 style={{ color: 'var(--highlight-color)', marginBottom: '1rem' }}>{t("categoriesHeading")}</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    {uniqueCategories.map((category, index) => (
                        <li key={index} style={{ marginBottom: '0.5rem' }}>
                            <Link
                                    to={`/genre/${encodeURIComponent(category.toLowerCase())}`}
                                style={{ color: 'var(--text-color)', textDecoration: 'none' }}
                            >
                                {t(category)}
                            </Link>
                        </li>
                    ))}
                </ul>
            </aside>

            {/* Book Gallery */}
            <section
                className="gallery"
                style={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '1rem',
                }}
            >
                {books.map((book) => {
                    const coverImage = book.source === 'local'
                        ? '/images/Hermes44.webp'
                        : (book.cover
                            ? (book.cover.startsWith('http') ? book.cover : `${process.env.REACT_APP_S3_BUCKET_URL}${book.cover}`)
                            : '/images/PlaceholderJPG.webp');

                    if (process.env.NODE_ENV !== 'production') {
                        console.log("🔍 Book Debug Info");
                        console.log("📖 Title:", book.title);
                        console.log("🆔 ID:", book.id);
                        console.log("🌍 Source:", book.source);
                        console.log("🖼️ Cover Image Assigned:", coverImage);
                    }

                    return (
                        <Link
							key={book.id}
							to={`/book/${book.titleSlug || book.id}`}
							className="book-link"
							style={{ textDecoration: 'none', color: 'inherit' }}
						>
							{/* 
							🧩 Restore this logic later if local books return:
							to={book.source === 's3'
								? `/book/${book.titleSlug || book.id}`
								: `/productpage/${book.id}`}
							*/}
                            <div
                                className="thumbnail"
                                style={{
									position: 'relative',           // ✅ Add this
									zIndex: 1,                      // ✅ Add this
                                    border: '1px solid var(--highlight-color)',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    backgroundColor: 'var(--light-gray)',
                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                <div
                                    className="cover"
                                    style={{
                                        backgroundImage: `url('${coverImage}')`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        height: '200px',
                                        borderRadius: '4px',
                                        marginBottom: '1rem',
                                        pointerEvents: 'none',  // ✅ add this
                                        position: 'relative',   // ✅ defensive stacking fix
                                        zIndex: 0,
                                    }}
                                />
                                <h2
                                    style={{
                                        fontSize: '1rem',
                                        marginBottom: '0.5rem',
                                        color: 'var(--highlight-color)',
                                        textAlign: 'center',
                                        lineHeight: '1.1',
                                    }}
                                >
                                    {truncateText(book.title, 29)}
                                </h2>
                                <p
                                    className="author"
                                    style={{
                                        fontSize: '0.9rem',
                                        color: 'var(--text-color)',
                                        textAlign: 'center',
                                    }}
                                >
                                    {truncateText(book.author, 24)}
                                </p>
                            </div>
                        </Link>
                    );
                })}
            </section>
        </main>
    );
};

export default HomePage;